import React, { useState, useEffect, useCallback, useContext, memo } from 'react';
import _ from 'lodash';
import AutoComplete from 'material-ui/AutoComplete';
import { RaisedButton } from 'material-ui';
import api from 'services';
import { ContextDispatchHireOrders } from './LetterHire';
import { withRouter } from 'react-router';

export function generateSpeedConsString(s) {
    const ifoString = s.ifo ? `${s.ifo.typeIfo || ''} - ${s.ifo.cons || ''}` : '';
    const mdoString = s.mdo ? `${s.mdo.typeMdo || ''} - ${s.mdo.cons || ''}` : '';
    
    return `Mode: ${s.mode} | Speed: ${s.speed || '-'} | Ifo: ${ifoString} | Mdo: ${mdoString}`;
}

const VesselInfo = (props) => {
    const [vesselNames, setVesselNames] = useState([]);
    /** @type {import('./hireOrdersReducer').TDispatchHireOrders} */
    const dispatchHireOrders = useContext(ContextDispatchHireOrders);
    const [hireOrder, setHireOrder] = useState(props.hireOrder);

    const dAutocomplete = useCallback(_.debounce((searchText) => {
        api.findVessel(searchText)
            .then((response) => {
                const _vesselNames = response.data.data.map(vessel => ({
                    name: `${vessel.name} (imoNumber - ${vessel.imoNumber})`,
                    item: vessel,
                }));

                setVesselNames(_vesselNames);
            })
            .catch(() => null);
    }, 1000), [props]);

    useEffect(() => {
        setHireOrder(props.hireOrder);
    }, [props.hireOrder]);

    const changeVesselName = (searchText) => {
        const newHireOrder = _.cloneDeep(hireOrder);
        newHireOrder.vesselName = searchText;
        dispatchHireOrders({ 
            type: 'UPDATE_ORDER', 
            payload: { index: props.orderIndex, value: newHireOrder },
        });
        dAutocomplete(searchText);
    };

    const chooseVessel = (updateProperties) => {
        if (!updateProperties) {
            return;
        }

        const newHireOrder = _.cloneDeep(hireOrder);

        newHireOrder.vesselName = updateProperties.name;

        const vesselData = _.pick(
            updateProperties.item,
            [
                '_id', 'imoNumber', 'deadWeight', 'blt', 'capacityV2', 'typeByConstruction',
                'geared', 'speedConsumptionV2', 'gearV2',
            ],
        );

        newHireOrder.vesselData = {
            _id: vesselData._id,
            sDWT: vesselData.deadWeight.sDWT,
            imoNumber: vesselData.imoNumber,
            blt: vesselData.blt,
            geared: typeof vesselData.geared === 'boolean' ? vesselData.geared : undefined,
            typeByConstruction: vesselData.typeByConstruction,
        };
        
        if (vesselData.capacityV2) {
            newHireOrder.vesselData.capacityV2Bale = vesselData.capacityV2.bale;
            newHireOrder.vesselData.capacityV2Grain = vesselData.capacityV2.grain;
        }

        if (
            vesselData.gearV2 &&
            vesselData.gearV2.summary && 
            vesselData.gearV2.summary.length
        ) {
            newHireOrder.vesselData.maxCraneCapacity = vesselData.gearV2.summary
                .reduce((m, s) => (s.capacity > m ? s.capacity : m), 0);
        }

        if (vesselData.speedConsumptionV2 && vesselData.speedConsumptionV2.length) {
            newHireOrder.vesselData.speedConsumptionV2 = vesselData.speedConsumptionV2
                .filter(s => ['Laden', 'Ballast', 'Eco'].includes(s.mode))
                .map(s => generateSpeedConsString(s));
        }

        dispatchHireOrders({ 
            type: 'UPDATE_ORDER', 
            payload: { index: props.orderIndex, value: newHireOrder },
        });
    };
    
    /**
     * @param {string} label 
     * @param {string[] | string} value 
     */
    const renderInfo = (label, value) => {
        if (Array.isArray(value)) {
            return (
                <div className='row' style={{ padding: '0 10px' }}>
                    <div className='column-left' style={{ alignSelf: 'baseline' }}>
                        {label}
                    </div>
                    <div className='column-right'>
                        { value.map(v => <div key={v} className='label'>{v}</div>) }
                    </div>
                </div>
            );
        }
        return (
            <div className='row' style={{ padding: '0 10px' }}>
                <div className='column-left' style={{ alignSelf: 'baseline' }}>
                    {label}
                </div>
                <div className='column-right'>
                    <div className='label'>
                        {value}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div>
            <div className='row' style={{ padding: '0 10px' }}>
                <div className='column-left'>
                    Vessel name:
                </div>
                <div className='column-right' style={{ display: 'flex' }}>
                    <AutoComplete
                        name='vesselName'
                        textFieldStyle={{ fontSize: '13px' }}
                        hintText='Vessel name'
                        searchText={props.hireOrder.vesselName || ''}
                        dataSource={vesselNames}
                        dataSourceConfig={{ text: 'name', value: 'item' }}
                        onUpdateInput={changeVesselName}
                        onNewRequest={chooseVessel}
                        filter={AutoComplete.noFilter}
                        fullWidth
                        listStyle={{ overflow: 'auto', maxHeight: '300px' }}
                        popoverProps={{
                            canAutoPosition: true,
                        }}
                        style={{ display: 'flex', width: '70%', marginRight: '10px' }}
                    />
                    <RaisedButton
                        labelStyle={{ fontSize: '10px' }}
                        style={{ height: '25px', margin: 'auto 0' }}
                        buttonStyle={{ height: '25px', lineHeight: '25px' }}
                        label='Composition'
                        disabled={!props.hireOrder.vesselId}
                        onClick={() => { 
                            let url = `emailId=${props.params.id}`;
                            if (props.hireOrder.vesselId && props.hireOrder.vesselName) {
                                url += `&_id=${props.hireOrder.vesselId}&name=${props.hireOrder.vesselName}`;
                            }
                            window.open(`${window.location.origin}/vessels/composition?${url}`, '_blank');
                        }}
                        secondary
                    />
                </div>
            </div>

            { renderInfo('imoNumber:', hireOrder.vesselData.imoNumber) }
            { renderInfo('DWT:', hireOrder.vesselData.sDWT) }
            { renderInfo('BLT:', hireOrder.vesselData.blt) }
            { renderInfo('Geared:', hireOrder.vesselData.imoNumber ? String(hireOrder.vesselData.geared) : '') }
            { renderInfo('Type By Construction:', hireOrder.vesselData.typeByConstruction) }
            { renderInfo('Max Crane Capacity:', hireOrder.vesselData.maxCraneCapacity) }
            { renderInfo('Speed & Consumption:', hireOrder.vesselData.speedConsumptionV2) }
        </div>
    );
};

export default memo(withRouter(VesselInfo));



// WEBPACK FOOTER //
// ./app/components/Letters/SingleNew/Hire/VesselInfo.jsx